import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession, useAuth } from '@/contexts/AuthContext';
import { Button } from './ui';

interface TopNavProps {
  transparent?: boolean;
}

export function TopNav({ transparent = false }: TopNavProps): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useSession();
  const { signOut } = useAuth();

  const isActive = (path: string): boolean => {
    return location.pathname === path;
  };

  const getLinkClassName = (path: string): string => {
    if (transparent) {
      const baseClasses = 'px-4 py-2 text-white hover:text-white/80 transition-colors';
      const activeClasses = 'font-bold';
      const inactiveClasses = 'font-normal';
      return isActive(path) ? `${baseClasses} ${activeClasses}` : `${baseClasses} ${inactiveClasses}`;
    }

    const baseClasses = 'px-4 py-2 text-gray-700 hover:text-primary transition-colors';
    const activeClasses = 'font-bold text-primary';
    const inactiveClasses = 'font-normal';

    return isActive(path) ? `${baseClasses} ${activeClasses}` : `${baseClasses} ${inactiveClasses}`;
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
    } catch (error) {
      console.error('Failed to sign out:', error);
    } finally {
      navigate('/');
    }
  };

  const navBgClass = transparent ? 'bg-transparent' : 'bg-white shadow-sm border-b border-gray-200';
  const brandClass = transparent ? 'text-white' : 'text-gray-900';
  const borderClass = transparent ? 'border-white/20' : 'border-gray-200';
  const userEmailClass = transparent ? 'text-white/80' : 'text-gray-600';
  const signOutClass = transparent ? 'text-white hover:text-white/80' : 'text-gray-700 hover:text-red-600';
  const signInClass = transparent ? 'text-white hover:text-white/80' : 'text-gray-700 hover:text-primary';

  return (
    <nav className={`${navBgClass} w-full`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center md:justify-between items-center h-16 relative">
          {/* Logo/Brand - Centred on mobile, left-aligned on desktop */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center gap-2">
              {/* Empty alt: decorative image, text label is adjacent for screen readers */}
              <img
                src="/savinhood-logo.webp"
                alt=""
                width="32"
                height="32"
                className="h-8 w-8"
              />
              <span className={`text-xl font-semibold ${brandClass}`}>
                Savin Hood
              </span>
            </Link>
          </div>

          {/* Navigation Links - Hidden on mobile, shown on tablet+ */}
          <div className="hidden md:flex items-center space-x-2">
            <Link to="/calculator" className={`${getLinkClassName('/calculator')} hidden md:block`}>
              Calculator
            </Link>
            <Link to="/contractor" className={`${getLinkClassName('/contractor')} hidden md:block`}>
              Contractor
            </Link>
            <Link to="/tracker" className={`${getLinkClassName('/tracker')} hidden md:block`}>
              Tracker
            </Link>
            {user && (
              <Link to="/settings" className={`${getLinkClassName('/settings')} hidden md:block`}>
                Settings
              </Link>
            )}

            {/* Auth Section */}
            {!loading && (
              <div className={`ml-4 pl-4 border-l ${borderClass} hidden md:block`}>
                {user ? (
                  <div className="flex items-center space-x-3">
                    <span className={`text-sm ${userEmailClass} max-w-[150px] truncate`} title={user.email}>
                      {user.email}
                    </span>
                    <Button
                      onClick={handleSignOut}
                      variant="link"
                      size="sm"
                      className={`${signOutClass} font-normal`}
                    >
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className={`px-3 py-1.5 text-sm ${signInClass} transition-colors font-normal`}
                  >
                    Sign In
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
